import React, { useState, useEffect } from 'react';
import { X, History, User, Calendar } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HistorialConfiguracionModal = ({ onClose }) => {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistorial();
  }, []);

  const fetchHistorial = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/gestor-citas/historial?limit=50`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setHistorial(data.historial || []);
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTipoCambioLabel = (tipo) => {
    const tipos = {
      'global': 'Configuración Global',
      'dia_especifico': 'Día Específico',
      'dia_no_operativo': 'Día No Operativo',
      'restaurar_default': 'Restaurar Default'
    };
    return tipos[tipo] || tipo;
  };

  const getTipoCambioColor = (tipo) => {
    const colores = {
      'global': 'bg-blue-500',
      'dia_especifico': 'bg-green-500',
      'dia_no_operativo': 'bg-red-500',
      'restaurar_default': 'bg-gray-500'
    };
    return colores[tipo] || 'bg-gray-500';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-gray-700 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <History className="w-6 h-6 text-yellow-500" />
            <div>
              <h2 className="text-2xl font-bold text-white">Historial de Cambios</h2>
              <p className="text-gray-400 text-sm">Últimas 50 modificaciones</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
              <p className="text-gray-400">Cargando historial...</p>
            </div>
          ) : historial.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No hay cambios registrados aún</p>
            </div>
          ) : (
            <div className="space-y-4">
              {historial.map((entry) => {
                const fecha = new Date(entry.timestamp);
                return (
                  <div
                    key={entry.id}
                    className="bg-slate-800 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors"
                  >
                    {/* Header del cambio */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className={`${getTipoCambioColor(entry.tipo_cambio)} px-3 py-1 rounded-full text-xs font-semibold text-white`}>
                          {getTipoCambioLabel(entry.tipo_cambio)}
                        </span>
                        {entry.fecha_afectada && (
                          <span className="flex items-center gap-1 text-sm text-gray-400">
                            <Calendar className="w-4 h-4" />
                            {entry.fecha_afectada}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {fecha.toLocaleString('es-ES')}
                      </span>
                    </div>

                    {/* Usuario */}
                    <div className="flex items-center gap-2 mb-3">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-white font-semibold">
                        {entry.usuario_nombre}
                      </span>
                      <span className="text-xs text-gray-500">#{entry.usuario_id.slice(0, 8)}</span>
                    </div>

                    {/* Detalles del cambio */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      {entry.valor_anterior && (
                        <div className="bg-slate-900 border border-gray-700 rounded p-3">
                          <p className="text-xs text-gray-400 mb-1">Valor Anterior</p>
                          <pre className="text-xs text-gray-300 overflow-x-auto">
                            {JSON.stringify(entry.valor_anterior, null, 2)}
                          </pre>
                        </div>
                      )}
                      <div className="bg-slate-900 border border-gray-700 rounded p-3">
                        <p className="text-xs text-gray-400 mb-1">Valor Nuevo</p>
                        <pre className="text-xs text-green-300 overflow-x-auto">
                          {JSON.stringify(entry.valor_nuevo, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900 border-t border-gray-700 p-6 flex justify-end">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-gray-700 text-gray-400 hover:bg-gray-800"
          >
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HistorialConfiguracionModal;